import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY_VIEW_MODE = 'draft_view_mode';
const STORAGE_KEY_NOTIFICATIONS = 'draft_notification_settings';

const DEFAULT_NOTIFICATION_SETTINGS = {
  soundEnabled: true,
  desktopEnabled: true
};

export const useDraftUI = () => {
  // Modal state
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [pendingRosterAssignment, setPendingRosterAssignment] = useState(null);
  const [selectedPlayerForModal, setSelectedPlayerForModal] = useState(null);

  // View mode with localStorage persistence
  const [viewMode, setViewModeState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_VIEW_MODE);
      return stored || 'table';
    } catch {
      return 'table';
    }
  });

  // Notification settings with localStorage persistence
  const [notificationSettings, setNotificationSettingsState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_NOTIFICATIONS);
      return stored ? JSON.parse(stored) : DEFAULT_NOTIFICATION_SETTINGS;
    } catch {
      return DEFAULT_NOTIFICATION_SETTINGS;
    }
  });

  // Persist view mode
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_VIEW_MODE, viewMode);
    } catch {
      // Ignore localStorage errors
    }
  }, [viewMode]);

  // Persist notification settings
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_NOTIFICATIONS, JSON.stringify(notificationSettings));
    } catch {
      // Ignore localStorage errors
    }
  }, [notificationSettings]);

  // Setters with type safety
  const setViewMode = useCallback((mode) => {
    if (mode === 'table' || mode === 'board') {
      setViewModeState(mode);
    }
  }, []);

  const setNotificationSettings = useCallback((updates) => {
    setNotificationSettingsState(prev => ({ ...prev, ...updates }));
  }, []);

  // Helper: Open position modal with player
  const openPositionModal = useCallback((player, assignment = null) => {
    setSelectedPlayerForModal(player);
    setPendingRosterAssignment(assignment);
    setShowPositionModal(true);
  }, []);

  // Helper: Close position modal and clear state
  const closePositionModal = useCallback(() => {
    setShowPositionModal(false);
    setPendingRosterAssignment(null);
  }, []);

  return {
    // Modal state
    showPositionModal,
    setShowPositionModal,
    pendingRosterAssignment,
    setPendingRosterAssignment,
    selectedPlayerForModal,
    setSelectedPlayerForModal,

    // Helpers
    openPositionModal,
    closePositionModal,

    // View mode
    viewMode,
    setViewMode,

    // Notifications
    notificationSettings,
    setNotificationSettings
  };
};
